import React, { Dispatch, SetStateAction, createContext, useEffect, useState } from "react";
import { fetchAssets, getPrinxUser, getTransactions, recordHeartbeat } from "calls";
import { usePrivy } from "@privy-io/react-auth";
import { AssetType, PrinxUserType, TransactionType, GeoType } from "types";
import { getUserEmail } from "common";
import { checkGeolocation } from "calls";
import { REACT_APP_ENV } from "env";
const currentPath = location.pathname;

interface PrinxContextType {
  //here is where we type all the state values and the functions
  assetData: AssetType[] | null;
  setAssets: React.Dispatch<React.SetStateAction<AssetType[]>>;
  prinxUser: PrinxUserType | null;
  setPrinxUser: React.Dispatch<React.SetStateAction<PrinxUserType | null>>;
  showPlaidKYC: boolean;
  setShowPlaidKYC: Dispatch<SetStateAction<boolean>>;
  showAttestationForm: boolean;
  setShowAttestationForm: Dispatch<SetStateAction<boolean>>;
  depositModalOpen: boolean;
  setDepositModalOpen: Dispatch<SetStateAction<boolean>>;
  setUserClosedPlaid: Dispatch<SetStateAction<boolean>>;
  userClosedPlaid: boolean;
  transactions: TransactionType[] | [];
  geo: GeoType | null;
  showWhitelistRejectModal: boolean;
  setShowWhitelistRejectModal: Dispatch<SetStateAction<boolean>>;
  showExitModal: boolean;
  setShowExitModal: Dispatch<SetStateAction<boolean>>;
}

const PrinxContext = createContext<PrinxContextType>({
  assetData: [],
  setAssets: () => null,
  prinxUser: null,
  setPrinxUser: () => null, // simplified placeholder
  showPlaidKYC: false,
  setShowPlaidKYC: () => null,
  showAttestationForm: false,
  setShowAttestationForm: () => null,
  depositModalOpen: false,
  setDepositModalOpen: () => null,
  transactions: [],
  setUserClosedPlaid: () => null,
  userClosedPlaid: false,
  geo: null,
  showWhitelistRejectModal: false,
  setShowWhitelistRejectModal: () => null,
  showExitModal: false,
  setShowExitModal: () => null
});

export const PrinxContextProvider = ({ children }: { children: React.ReactNode }) => {
  const [assets, setAssets] = useState<AssetType[]>([]);
  const { user } = usePrivy();
  const [prinxUser, setPrinxUser] = useState<PrinxContextType["prinxUser"]>(null);
  const [transactions, setTransactions] = useState<TransactionType[]>([]);
  const [, setTransactionsLoaded] = useState<boolean>(false);
  const [userClosedPlaid, setUserClosedPlaid] = useState(false);
  const [showExitModal, setShowExitModal] = useState(false);

  const [showWhitelistRejectModal, setShowWhitelistRejectModal] = useState(false);
  const [geo, setGeo] = useState(null);

  //The following fields are for the onboarding process
  const [showPlaidKYC, setShowPlaidKYC] = useState<boolean>(false);
  const [showAttestationForm, setShowAttestationForm] = useState(false);
  const [depositModalOpen, setDepositModalOpen] = useState(false);

  useEffect(() => {
    const getAssets = async () => {
      let assets = await fetchAssets();

      if (REACT_APP_ENV === "production") {
        assets = assets.filter((item: AssetType) => item.address !== undefined && item.isLive);
      } else if (REACT_APP_ENV === "pre_production") {
        assets = assets.filter((item: AssetType) => item.address !== undefined);
      }
      setAssets(assets); // Allow setting empty arrays too
    };

    getAssets(); // Always call once on mount
  }, []); // ✅ Prevents infinite loop

  //This useEffect will get prinxUser after Privy's user has initialized.
  useEffect(() => {
    async function fetchPrinxUser() {
      if (!user) return;

      const email = getUserEmail(user);
      if (!email) return;

      try {
        const userData = await getPrinxUser(user.id);
        if (userData) setPrinxUser(userData);
        if (userData) recordHeartbeat();
      } catch (error) {
        console.error("Failed to fetch in prinxDataContext:", error);
      }
    }
    fetchPrinxUser();
  }, [user]);

  useEffect(() => {
    async function fetchTransactions() {
      if (prinxUser) {
        try {
          const data = await getTransactions(prinxUser.smart_wallet);
          setTransactions(data.reverse());
          setTransactionsLoaded(true);
        } catch (error) {
          console.error("Failed to fetch transactions:", error);
        }
      }
    }
    if (prinxUser && prinxUser.smart_wallet) {
      fetchTransactions();
    }
  }, [prinxUser]);

  useEffect(() => {
    // if (isAlphaEnv && currentPath !== "/" && currentPath !== "/waitlist") {
    //   navigate("/waitlist");
    // }
    if (currentPath != "/not-here-yet") {
      checkGeolocation().then((data) => {
        if (data && data.geo) {
          setGeo(data.geo);
          // if (data.badLocation && config.geofence) {
          //   //navigate("/not-here-yet", { state: data });
          // }
        }
      });
    }
  }, [location]);

  const value = {
    assetData: assets,
    setAssets,
    prinxUser,
    setPrinxUser,
    showPlaidKYC,
    setShowPlaidKYC,
    showAttestationForm,
    setShowAttestationForm,
    depositModalOpen,
    setDepositModalOpen,
    transactions,
    userClosedPlaid,
    setUserClosedPlaid,
    geo,
    showExitModal,
    setShowExitModal,
    showWhitelistRejectModal,
    setShowWhitelistRejectModal
  };

  return <PrinxContext.Provider value={value}>{children}</PrinxContext.Provider>;
};

export default PrinxContext;
